import {
  dynamicElementsBadgeKey,
  dynamicElementsIconKey,
} from '@epicuro-next/constants/dynamic-elements';
import {
  productIconsLabels,
  productIconsTooltipLabels,
} from '@epicuro-next/constants/features/localization';
import { capitalizeFirstLetter } from '@epicuro-next/data/product';
import {
  IDynamicInput,
  IDynamicItem,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { IProductIconNames } from '@epicuro-next/models/products';
import {
  DynamicLocalePipe,
  translationNotResolved,
} from '@epicuro-next/platform/i18n';
import { ProductLabel, ProductLabelType } from 'graphql-api/gql-api-interfaces';

export enum ProductLabelLocation {
  Right = 'right',
  Top = 'top',
  All = 'all',
}

export const defaultProductLabelLocation = ProductLabelLocation.Right;

interface LabelData {
  classes?: string;
  style?: string;
  variables?: string;
  icon?: string;
  tooltip?: string;
  locale?: string;
  location?: ProductLabelLocation;

  color?: string;
  backgroundColor?: string;

  // value for locale expressions (can be discount value, or attributeValues)
  values?: string;
}

type ExtendedProductLabel = Omit<ProductLabel, 'data'> & {
  data: LabelData;
};

export function mapProductLabelToDynamicItem(
  label: ExtendedProductLabel,
): IDynamicItem | null {
  if (label.labelType === ProductLabelType.Badge) {
    return mapLabelToBadgeItem(label);
  }

  if (label.labelType === ProductLabelType.Icon) {
    return mapLabelToIconItem(label);
  }

  return null;
}

function mapLabelToIconItem(label: ExtendedProductLabel): IDynamicItem | null {
  const data = label.data;
  const classes = parseClasses(data.classes);
  const style = parseStyles(data);
  const variables = parseJson(data.variables, {});
  const badgeIcon = data.icon ? parseJson(data.icon, []) : undefined;

  if (badgeIcon?.length !== 0) {
    return {
      contentTypeId: dynamicElementsIconKey,
      styles: {
        classes,
        style,
        variables,
      },
      inputs: {
        tooltip: getTooltip(label) ?? '',
        icon: badgeIcon as IDynamicInput,
      },
    };
  }

  return null;
}

function mapLabelToBadgeItem(label: ExtendedProductLabel): IDynamicItem {
  const data = label.data;
  const classes = parseClasses(data.classes);
  const style = parseStyles(data);
  const variables = parseJson(data.variables, {});
  const badgeIcon = data.icon ? parseJson(data.icon, []) : undefined;

  return {
    contentTypeId: dynamicElementsBadgeKey,
    styles: {
      classes,
      style,
      variables,
    },
    inputs: {
      tooltip: getTooltip(label) ?? '',
      icon: badgeIcon as IDynamicInput,
      label: getLabelText(label),
    },
  };
}

function parseStyles(data: LabelData): { [key: string]: string } {
  const { color, backgroundColor, style } = data;
  return {
    ...parseJson(style, {}),
    ...(color
      ? {
          color,
        }
      : {}),
    ...(backgroundColor
      ? {
          backgroundColor,
        }
      : {}),
  };
}

function parseClasses(classes?: string): string[] {
  return (
    classes
      ?.split(',')
      .filter(Boolean)
      .map((v) => v.trim().toLowerCase()) ?? []
  );
}

function parseJson<T>(jsonString = '', defaultValue: T): T {
  if (!jsonString) {
    return defaultValue;
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error(
      `Error parsing JSON product label string: ${jsonString}`,
      error,
    );
    return defaultValue;
  }
}

function getLabelText(label: ExtendedProductLabel): string {
  const localeExpressions = label.data.values?.split(',').map((v) => v.trim());

  if (!label.data.locale) {
    return (
      productIconsLabels[label.name as IProductIconNames]?.(
        localeExpressions,
      ) ?? capitalizeFirstLetter(label.name)
    );
  }

  const localePipe = new DynamicLocalePipe();
  const translatedLabel = localePipe.transform(
    label.data.locale,
    localeExpressions,
  );
  return translationNotResolved(label.data.locale, translatedLabel)
    ? productIconsLabels[label.name as IProductIconNames]?.() ??
        capitalizeFirstLetter(label.data.locale ?? label.name)
    : translatedLabel;
}

function getTooltip(label: ProductLabel): string | undefined {
  const tooltipData = label.data.tooltip;
  if (tooltipData) {
    const localePipe = new DynamicLocalePipe();
    const translatedLabel = localePipe.transform(tooltipData);
    return translationNotResolved(tooltipData, translatedLabel)
      ? productIconsTooltipLabels[label.name as IProductIconNames] ??
          translatedLabel ??
          capitalizeFirstLetter(label.name)
      : translatedLabel;
  }
  return undefined;
}
